import { addDagen, weekdagIndex } from './datum';
import { Kalender } from './kalender';
import { niveau } from './suggestie';
import { geplandeUren } from './werkpakket';
import { agileUren } from './agile';
import type { ISODate, Project, Taak, Takenpakket } from './types';

export interface ExpertisePrognose {
  expertise: string;
  budget: number;
  ingepland: number;
  rest: number;
  team: number;
  /** beschikbaar tot de livegang (uren) */
  beschikbaar: number;
  klaar: ISODate | null;
  haalbaar: boolean | null;
}

export interface ProjectPrognose {
  perExpertise: ExpertisePrognose[];
  prognose: ISODate | null;
  bepalend: string | null;
  laterDanHorizon: boolean;
}

const HORIZON = 183;

/** Vrije bruikbare uren per engineer per dag (SPEC §3.9). */
function vrijeBlokken(kal: Kalender, dagen: ISODate[]): Map<string, number[]> {
  const min = kal.cfg.prognoseMinUren;
  const m = new Map<string, number[]>();
  for (const e of kal.engineers) {
    if (e.inzet === 'NIET_CAPACITEIT' || e.actief === false) continue;
    m.set(
      e.id,
      dagen.map((d) => {
        const v = Math.max(0, kal.vrij(e, d));
        return v >= min - 0.01 ? v : 0;
      }),
    );
  }
  return m;
}

export function horizonDagen(kal: Kalender, start: ISODate): ISODate[] {
  const uit: ISODate[] = [];
  for (let i = 0; i < HORIZON; i++) {
    const d = addDagen(start, i);
    if (weekdagIndex(d) <= 4 && !kal.isHeleFeestdag(d)) uit.push(d);
  }
  return uit;
}

/**
 * SPEC §3.9 – prognose van een project. `blokken` mag hergebruikt worden voor meerdere projecten.
 * `tps`: takenpakketten; items van agile werkpakketten in een sprint tellen als ingepland (§9.2).
 */
export function projectPrognose(kal: Kalender, p: Project, taken: Taak[], vandaag: ISODate, cache?: { dagen: ISODate[]; blokken: Map<string, number[]> }, tps: Takenpakket[] = []): ProjectPrognose {
  const pItems = tps.filter((t) => t.projectId === p.id);
  const start = p.start && p.start > vandaag ? p.start : vandaag;
  const dagen = cache?.dagen ?? horizonDagen(kal, start);
  const blokken = cache?.blokken ?? vrijeBlokken(kal, dagen);
  const offset = cache ? Math.max(0, dagen.findIndex((d) => d >= start)) : 0;
  const pTaken = taken.filter((t) => t.projectId === p.id);
  const inzet = Math.max(1, Math.min(100, kal.cfg.prognoseInzet)) / 100;
  const maxTeam = kal.cfg.prognoseMaxEngineers;
  const per: ExpertisePrognose[] = p.expertiseBudget.map((b) => {
    const ingepland = geplandeUren(pTaken.filter((t) => (t.expertises ?? []).some((x) => x.naam === b.naam))) + agileUren(pItems.filter((i) => i.expertise === b.naam)).ingepland;
    const rest = Math.max(0, b.uren - ingepland);
    const kand = kal.engineers
      .filter((e) => blokken.has(e.id) && e.expertises.some((x) => x.naam === b.naam))
      .map((e) => {
        const v = blokken.get(e.id)!.slice(offset);
        return { v, s: (niveau(e, b.naam) ?? 100) * 10000 + v.reduce((a, c) => a + c, 0) };
      })
      .sort((a, c) => c.s - a.s);
    const team = maxTeam <= 0 ? kand : kand.slice(0, maxTeam);
    let cum = 0;
    let klaar: ISODate | null = rest <= 0.01 ? start : null;
    let beschikbaar = 0;
    const ds = dagen.slice(offset);
    for (let i = 0; i < ds.length; i++) {
      const dv = team.reduce((s, t) => s + (t.v[i] ?? 0), 0) * inzet;
      cum += dv;
      if (p.livegang && ds[i] <= p.livegang) beschikbaar += dv;
      if (!klaar && cum >= rest - 0.01) klaar = ds[i];
    }
    return {
      expertise: b.naam,
      budget: b.uren,
      ingepland: Math.round(ingepland * 100) / 100,
      rest: Math.round(rest * 100) / 100,
      team: team.length,
      beschikbaar: Math.round(beschikbaar * 10) / 10,
      klaar,
      haalbaar: p.livegang ? beschikbaar >= rest - 0.01 : null,
    };
  });
  let prognose: ISODate | null = null;
  let bepalend: string | null = null;
  let later = false;
  for (const x of per) {
    if (x.rest <= 0.01) continue;
    if (!x.klaar) later = true;
    else if (!prognose || x.klaar > prognose) {
      prognose = x.klaar;
      bepalend = x.expertise;
    }
  }
  return { perExpertise: per, prognose: later ? null : prognose, bepalend, laterDanHorizon: later };
}

export function prognoseCache(kal: Kalender, vandaag: ISODate) {
  const dagen = horizonDagen(kal, vandaag);
  return { dagen, blokken: vrijeBlokken(kal, dagen) };
}
